import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "utils/api";
import { useTeamLocale } from "./TeamLocaleContext";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// CRA + Leaflet default icon paths break without this.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/** Dr. Hoffmann studio — Cremon 11, 20457 Hamburg */
const HQ = {
  id: "hq-cremon-11",
  lat: 53.5454515,
  lng: 9.9885532,
  companyName: "Dr. Hoffmann Massage",
  addressLine: "Cremon 11, 20457 Hamburg",
  phone: "",
  dealStageLabel: "Standort",
};

/** Company visits in the generated route (final stop is always back to start). */
const MAX_ROUTE_STOPS = 24;
const UNKNOWN_STAGE_LABEL = "__unknown__";
const LOST_STAGE_LABEL = "Verloren";

const STAGE_COLORS = {
  Gold: "#c9a227",
  Silber: "#7a8699",
  Silver: "#7a8699",
  Bronze: "#a65c2e",
  [LOST_STAGE_LABEL]: "#dc2626",
  Lost: "#dc2626",
};

const LOST_STAGE_COLOR = "#dc2626";
const UNKNOWN_STAGE_COLOR = "#111827";
const LOST_STAGE_HINTS = /verloren|lost|closed[\s_-]*lost/i;
const FILTER_STAGE_LABELS = ["Bronze", "Silber", "Gold", UNKNOWN_STAGE_LABEL, LOST_STAGE_LABEL];
const KNOWN_STAGE_LABELS = new Set(["Bronze", "Silber", "Silver", "Gold"]);
const CRM_STAGE_OPTIONS = ["bronze", "silber", "gold", "unknown", "lost"];

const colorForStage = (label) => {
  if (LOST_STAGE_HINTS.test(String(label || ""))) return LOST_STAGE_COLOR;
  if (!label || label === UNKNOWN_STAGE_LABEL) return UNKNOWN_STAGE_COLOR;
  if (STAGE_COLORS[label]) return STAGE_COLORS[label];
  return UNKNOWN_STAGE_COLOR;
};

const isLostPin = (pin) =>
  LOST_STAGE_HINTS.test(
    String(pin?.dealStageLabel || pin?.dealStage || pin?.stageLabel || pin?.stage || "")
  );

const stageKeyForPin = (pin) => {
  if (isLostPin(pin)) return LOST_STAGE_LABEL;
  if (pin?.dealStageLabel === "Silver") return "Silber";
  if (KNOWN_STAGE_LABELS.has(pin?.dealStageLabel)) return pin.dealStageLabel;
  return UNKNOWN_STAGE_LABEL;
};

const displayStageForPin = (pin) => {
  if (isLostPin(pin)) return LOST_STAGE_LABEL;
  return pin?.dealStageLabel || "";
};

const colorForPin = (pin) => colorForStage(stageKeyForPin(pin));

const stageValueForPin = (pin) => {
  const key = stageKeyForPin(pin);
  if (key === "Bronze") return "bronze";
  if (key === "Silber" || key === "Silver") return "silber";
  if (key === "Gold") return "gold";
  if (key === LOST_STAGE_LABEL) return "lost";
  return "unknown";
};

const pinDisplayName = (pin) =>
  pin?.companyName ||
  pin?.contactName ||
  pin?.contactFullName ||
  pin?.fullName ||
  pin?.dealName ||
  "";

const getFullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || null;

const pinNameSortValue = (pin) => pinDisplayName(pin).toLowerCase();

const lastContactMs = (pin) => {
  const time = pin?.lastContactedAt ? new Date(pin.lastContactedAt).getTime() : NaN;
  return Number.isFinite(time) ? time : null;
};

const sortPins = (items, sortMode) => {
  const next = [...items];
  if (sortMode === "last-contacted-asc" || sortMode === "last-contacted-desc") {
    const direction = sortMode === "last-contacted-asc" ? 1 : -1;
    return next.sort((a, b) => {
      const ta = lastContactMs(a);
      const tb = lastContactMs(b);
      if (ta === null && tb === null) {
        return pinNameSortValue(a).localeCompare(pinNameSortValue(b), "de");
      }
      if (ta === null) return 1;
      if (tb === null) return -1;
      return (ta - tb) * direction || pinNameSortValue(a).localeCompare(pinNameSortValue(b), "de");
    });
  }
  return next.sort((a, b) => pinNameSortValue(a).localeCompare(pinNameSortValue(b), "de"));
};

const requestFullscreen = (element) => {
  if (element.requestFullscreen) {
    return element
      .requestFullscreen({ navigationUI: "hide" })
      .catch(() => element.requestFullscreen());
  }
  if (element.webkitRequestFullscreen) return element.webkitRequestFullscreen();
  return Promise.reject(new Error("Fullscreen unavailable"));
};

const exitFullscreen = () => {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  return Promise.resolve();
};

const invalidateMapIfReady = (map) => {
  try {
    const container = map?.getContainer?.();
    if (!container?.isConnected || !map?._mapPane) return;
    map.invalidateSize();
  } catch {
    // Leaflet can throw during React dev remounts while panes are being rebuilt.
  }
};

const applyMapFullscreenViewport = () => {
  const root = document.documentElement;
  const viewport = window.visualViewport;
  root.style.setProperty(
    "--team-deal-map-fullscreen-height",
    `${Math.round(viewport?.height || window.innerHeight)}px`
  );
  root.style.setProperty(
    "--team-deal-map-fullscreen-width",
    `${Math.round(viewport?.width || window.innerWidth)}px`
  );
  root.style.setProperty(
    "--team-deal-map-fullscreen-top",
    `${Math.round(viewport?.offsetTop || 0)}px`
  );
  root.style.setProperty(
    "--team-deal-map-fullscreen-left",
    `${Math.round(viewport?.offsetLeft || 0)}px`
  );
};

const clearMapFullscreenViewport = () => {
  const root = document.documentElement;
  root.style.removeProperty("--team-deal-map-fullscreen-height");
  root.style.removeProperty("--team-deal-map-fullscreen-width");
  root.style.removeProperty("--team-deal-map-fullscreen-top");
  root.style.removeProperty("--team-deal-map-fullscreen-left");
};

const makeStageIcon = (color) =>
  L.divIcon({
    className: "team-deal-map-marker",
    html: `<span style="--pin-color:${color}"></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -10],
  });

const makeHqIcon = () =>
  L.divIcon({
    className: "team-deal-map-marker team-deal-map-marker--hq",
    html: `<span class="team-deal-map-hq-pin" title="Cremon 11"></span>`,
    iconSize: [28, 36],
    iconAnchor: [14, 34],
    popupAnchor: [0, -30],
  });

const makeUserIcon = () =>
  L.divIcon({
    className: "team-deal-map-marker team-deal-map-marker--user",
    html: `<span class="team-deal-map-user-pin" aria-hidden="true"><i></i></span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatSyncTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeWebsiteUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withProtocol);
    if (!/^https?:$/i.test(url.protocol)) return null;
    return url.toString();
  } catch {
    return null;
  }
};

const websiteLabel = (value) =>
  String(value || "")
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");

const pinWebsiteValue = (pin) => {
  const candidates = [pin?.website, pin?.domain].filter(Boolean);
  return candidates.find((value) => normalizeWebsiteUrl(value)) || "";
};

/** Approximate distance in meters (good enough for Hamburg-scale routing). */
const distanceMeters = (a, b) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

const pinAddressLine = (pin) =>
  String(pin.addressLine || pin.address?.fullAddress || "").trim();

/** True when lat/lng can be passed to Leaflet. Number(null) === 0, so never wrap null in Number(). */
const hasMapCoords = (pin) => {
  const lat = pin?.lat;
  const lng = pin?.lng;
  if (lat == null || lng == null || lat === "" || lng === "") return false;
  return Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
};

/** Normalize address text so "Str." / punctuation variants still match. */
const normalizeAddressKey = (address) =>
  String(address || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\b(strasse|straße|str\.)\b/g, "str")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Collapse pins that share the same address into one stop.
 * Falls back to exact lat/lng only when no address is available.
 */
const groupPinsByLocation = (pins) => {
  const groups = new Map();

  pins.forEach((pin) => {
    if (!hasMapCoords(pin)) return;
    const lat = Number(pin.lat);
    const lng = Number(pin.lng);

    const addressLine = pinAddressLine(pin);
    const addressKey = normalizeAddressKey(addressLine);
    const key = addressKey
      ? `addr:${addressKey}`
      : `geo:${lat.toFixed(6)},${lng.toFixed(6)}`;

    const companyName = pinDisplayName(pin);
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        key,
        lat,
        lng,
        addressLine,
        companies: [
          {
            id: pin.id,
            companyName,
            dealStageLabel: displayStageForPin(pin),
            phone: pin.phone || "",
          },
        ],
      });
      return;
    }

    if (!existing.addressLine && addressLine) existing.addressLine = addressLine;
    const already = existing.companies.some(
      (c) =>
        c.id === pin.id ||
        (companyName &&
          c.companyName.toLowerCase() === companyName.toLowerCase())
    );
    if (!already) {
      existing.companies.push({
        id: pin.id,
        companyName,
        dealStageLabel: displayStageForPin(pin),
        phone: pin.phone || "",
      });
    }
  });

  return [...groups.values()];
};

const HQ_ADDRESS_KEY = normalizeAddressKey(HQ.addressLine);

const stopMatchesOrigin = (stop, origin) => {
  const stopAddr = normalizeAddressKey(stop.addressLine);
  const originAddr = normalizeAddressKey(origin.addressLine || "");
  if (stopAddr && originAddr && stopAddr === originAddr) return true;

  // Always skip Cremon 11 when routing from HQ (even if address text differs slightly).
  if (originAddr === HQ_ADDRESS_KEY || origin.lat === HQ.lat) {
    if (stopAddr === HQ_ADDRESS_KEY) return true;
    if (
      Number.isFinite(stop.lat) &&
      Number.isFinite(stop.lng) &&
      distanceMeters(stop, { lat: HQ.lat, lng: HQ.lng }) < 40
    ) {
      return true;
    }
    const names = (stop.companies || []).map((c) =>
      String(c.companyName || "").toLowerCase()
    );
    if (names.some((n) => n.includes("dr. hoffmann") || n.includes("dr hoffmann"))) {
      return true;
    }
  }

  if (
    Number.isFinite(stop.lat) &&
    Number.isFinite(origin.lat) &&
    distanceMeters(stop, origin) < 25
  ) {
    return true;
  }
  return false;
};

/**
 * Closed tour length: origin → stops… → origin.
 */
const closedTourLength = (origin, stops) => {
  if (!stops.length) return 0;
  let len = distanceMeters(origin, stops[0]);
  for (let i = 0; i < stops.length - 1; i += 1) {
    len += distanceMeters(stops[i], stops[i + 1]);
  }
  len += distanceMeters(stops[stops.length - 1], origin);
  return len;
};

/**
 * 2-opt local search — untangles crossings and shortens the return-to-start leg.
 * Classic improvement for TSP-style loops (what nearest-neighbor is missing).
 */
const improveTourWith2Opt = (origin, stops) => {
  if (stops.length < 3) return stops;

  let route = [...stops];
  let improved = true;

  while (improved) {
    improved = false;
    for (let i = 0; i < route.length - 1; i += 1) {
      for (let k = i + 1; k < route.length; k += 1) {
        const a = i === 0 ? origin : route[i - 1];
        const b = route[i];
        const c = route[k];
        const d = k === route.length - 1 ? origin : route[k + 1];

        const before = distanceMeters(a, b) + distanceMeters(c, d);
        const after = distanceMeters(a, c) + distanceMeters(b, d);
        if (after + 0.5 < before) {
          route = [
            ...route.slice(0, i),
            ...route.slice(i, k + 1).reverse(),
            ...route.slice(k + 1),
          ];
          improved = true;
        }
      }
    }
  }

  return route;
};

/** Order stops around their centroid (circular / radial sweep). */
const orderByPolarAngle = (stops) => {
  if (stops.length < 2) return [...stops];
  const cx = stops.reduce((s, p) => s + p.lat, 0) / stops.length;
  const cy = stops.reduce((s, p) => s + p.lng, 0) / stops.length;
  return [...stops].sort(
    (a, b) =>
      Math.atan2(a.lng - cy, a.lat - cx) - Math.atan2(b.lng - cy, b.lat - cx)
  );
};

const buildNearestNeighborOrder = (origin, pool) => {
  const remaining = [...pool];
  const stops = [];
  let current = { lat: origin.lat, lng: origin.lng };

  while (remaining.length) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const d = distanceMeters(current, remaining[i]);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    stops.push(next);
    current = next;
  }

  return stops;
};

/**
 * Build a loop-friendly route (TSP-style):
 * 1) Take the closest N places to the start (work area)
 * 2) Order them as a circle (polar angle) and also via nearest-neighbor
 * 3) Improve both with 2-opt; keep the shorter closed tour
 *
 * That puts stops on the way out and back, instead of one long final jump home.
 */
const buildOptimizedRoute = (origin, candidates, maxStops = MAX_ROUTE_STOPS) => {
  const pool = candidates
    .filter((c) => !stopMatchesOrigin(c, origin))
    .sort(
      (a, b) => distanceMeters(origin, a) - distanceMeters(origin, b)
    )
    .slice(0, maxStops);

  if (!pool.length) return [];

  const candidatesOrders = [
    orderByPolarAngle(pool),
    buildNearestNeighborOrder(origin, pool),
  ].map((order) => improveTourWith2Opt(origin, order));

  let best = candidatesOrders[0];
  let bestLen = closedTourLength(origin, best);
  for (let i = 1; i < candidatesOrders.length; i += 1) {
    const len = closedTourLength(origin, candidatesOrders[i]);
    if (len < bestLen) {
      best = candidatesOrders[i];
      bestLen = len;
    }
  }

  return best;
};

const APPLE_ROUTE_MODE = "cycling";
const GOOGLE_ROUTE_MODE = "bicycling";

const formatRouteCoord = (point) =>
  `${Number(point.lat).toFixed(6)},${Number(point.lng).toFixed(6)}`;

const buildGoogleMapsRouteUrl = (origin, stops) => {
  const parts = [
    formatRouteCoord(origin),
    ...stops.map(formatRouteCoord),
    formatRouteCoord(origin),
  ];
  return `https://www.google.com/maps/dir/${parts.join("/")}?travelmode=${GOOGLE_ROUTE_MODE}`;
};

const buildAppleMapsRouteUrl = (origin, stops) => {
  const params = new URLSearchParams();
  params.set("source", formatRouteCoord(origin));
  params.set("mode", APPLE_ROUTE_MODE);
  if (stops.length) {
    const last = stops[stops.length - 1];
    stops.slice(0, -1).forEach((stop) => params.append("waypoint", formatRouteCoord(stop)));
    params.set("destination", formatRouteCoord(last));
  } else {
    params.set("destination", formatRouteCoord(origin));
  }
  return `https://maps.apple.com/directions?${params.toString()}`;
};

const buildRouteMapUrls = (origin, stops) => ({
  appleMapsUrl: buildAppleMapsRouteUrl(origin, stops),
  googleMapsUrl: buildGoogleMapsRouteUrl(origin, stops),
});

const originFromRouteStop = (stop, t) => ({
  lat: stop.lat,
  lng: stop.lng,
  label:
    stop.companies?.[0]?.companyName ||
    stop.label ||
    t("dealMap.unnamed"),
  addressLine: stop.addressLine || "",
});

const buildPopupHtml = (pin, t, isHq = false) => {
  const name = escapeHtml(pinDisplayName(pin) || t("dealMap.unnamed"));
  const address = escapeHtml(
    pin.addressLine || pin.address?.fullAddress || t("dealMap.noAddress")
  );
  if (isHq) {
    return (
      `<strong>${name}</strong><br/>` +
      `${address}<br/>` +
      `<em>${escapeHtml(t("dealMap.hqLabel"))}</em>`
    );
  }
  const phone = escapeHtml(pin.phone || t("dealMap.noPhone"));
  const stage = escapeHtml(displayStageForPin(pin) || t("dealMap.unknownStage"));
  const lastContacted = escapeHtml(
    formatSyncTime(pin.lastContactedAt) || t("dealMap.noLastContact")
  );
  const websiteValue = pinWebsiteValue(pin);
  const websiteUrl = normalizeWebsiteUrl(websiteValue);
  const websiteText = websiteUrl
    ? `<a href="${escapeHtml(websiteUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(
        websiteLabel(websiteValue)
      )}</a>`
    : escapeHtml(t("dealMap.noWebsite"));
  return (
    `<strong>${name}</strong><br/>` +
    `${address}<br/>` +
    `${escapeHtml(t("dealMap.phone"))}: ${phone}<br/>` +
    `${escapeHtml(t("dealMap.lastContactedAt"))}: ${lastContacted}<br/>` +
    `${escapeHtml(t("dealMap.website"))}: ${websiteText}<br/>` +
    `<em>${escapeHtml(t("dealMap.stage"))}: ${stage}</em>`
  );
};

const DealMapTab = () => {
  const { t } = useTeamLocale();
  const mapShellRef = useRef(null);
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const hqLayerRef = useRef(null);
  const userLayerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const markersRef = useRef(new Map());
  const geoWatchIdRef = useRef(null);

  const [pins, setPins] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    withCoordinates: 0,
    missingCoordinates: 0,
    stages: [],
    lastSyncedAt: null,
  });
  const [stageFilter, setStageFilter] = useState("all");
  const [sortMode, setSortMode] = useState("name-asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stageSavingId, setStageSavingId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [geoStatus, setGeoStatus] = useState("pending"); // pending | granted | denied | unavailable
  const [routeMessage, setRouteMessage] = useState(null);
  const [routePlan, setRoutePlan] = useState(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const nativeFullscreenRef = useRef(false);
  const isMapImmersive = isMapFullscreen;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/hubspot/deal-pins", {
        params: { brand: "dr-hoffmann", includeArchived: "true" },
      });
      setPins(data.pins || []);
      setMeta(
        data.meta || {
          total: 0,
          withCoordinates: 0,
          missingCoordinates: 0,
          stages: [],
          lastSyncedAt: null,
        }
      );
    } catch (err) {
      setError(err?.response?.data?.message || t("dealMap.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const updatePinStage = async (pinId, stage) => {
    if (!pinId) return;
    setStageSavingId(pinId);
    setError(null);
    try {
      const { data } = await api.patch(`/admin/hubspot/deal-pins/${pinId}/stage`, {
        stage,
      });
      if (data.pin) {
        setPins((prev) => prev.map((pin) => (pin.id === pinId ? data.pin : pin)));
        setMeta((prev) => ({
          ...prev,
          lastSyncedAt: data.pin.lastSyncedAt || prev.lastSyncedAt,
        }));
      }
    } catch (err) {
      setError(err?.response?.data?.message || t("dealMap.stageUpdateError"));
    } finally {
      setStageSavingId(null);
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const native = getFullscreenElement() === mapShellRef.current;
      if (native) {
        nativeFullscreenRef.current = true;
        setIsMapFullscreen(true);
      } else if (nativeFullscreenRef.current) {
        nativeFullscreenRef.current = false;
        setIsMapFullscreen(false);
      }
      setTimeout(() => invalidateMapIfReady(mapRef.current), 80);
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle(
      "team-deal-map-document-fullscreen",
      isMapImmersive
    );
    document.body.classList.toggle("team-deal-map-body-fullscreen", isMapImmersive);
    const timer = setTimeout(() => invalidateMapIfReady(mapRef.current), 80);

    return () => {
      clearTimeout(timer);
      document.documentElement.classList.remove("team-deal-map-document-fullscreen");
      document.body.classList.remove("team-deal-map-body-fullscreen");
    };
  }, [isMapImmersive]);

  useEffect(() => {
    if (!isMapImmersive) return undefined;

    const updateFullscreenHeight = () => {
      applyMapFullscreenViewport();
      setTimeout(() => invalidateMapIfReady(mapRef.current), 40);
    };

    updateFullscreenHeight();
    window.addEventListener("resize", updateFullscreenHeight);
    window.addEventListener("orientationchange", updateFullscreenHeight);
    window.visualViewport?.addEventListener("resize", updateFullscreenHeight);
    window.visualViewport?.addEventListener("scroll", updateFullscreenHeight);

    return () => {
      window.removeEventListener("resize", updateFullscreenHeight);
      window.removeEventListener("orientationchange", updateFullscreenHeight);
      window.visualViewport?.removeEventListener("resize", updateFullscreenHeight);
      window.visualViewport?.removeEventListener("scroll", updateFullscreenHeight);
      clearMapFullscreenViewport();
    };
  }, [isMapImmersive]);

  const requestUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoStatus("unavailable");
      return;
    }

    setGeoStatus("pending");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setGeoStatus("granted");
      },
      (err) => {
        setGeoStatus(err?.code === 1 ? "denied" : "unavailable");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );

    if (geoWatchIdRef.current != null) {
      navigator.geolocation.clearWatch(geoWatchIdRef.current);
    }
    geoWatchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setGeoStatus("granted");
      },
      () => {
        /* keep last known; getCurrentPosition already set status */
      },
      { enableHighAccuracy: true, maximumAge: 15000 }
    );
  }, []);

  useEffect(() => {
    requestUserLocation();
    return () => {
      if (geoWatchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(geoWatchIdRef.current);
        geoWatchIdRef.current = null;
      }
    };
  }, [requestUserLocation]);

  const legendStages = FILTER_STAGE_LABELS;
  const lastSyncedLabel = formatSyncTime(meta.lastSyncedAt);

  const filteredPins = useMemo(() => {
    if (stageFilter === "all") return pins;
    return pins.filter((p) => stageKeyForPin(p) === stageFilter);
  }, [pins, stageFilter]);

  const sortedPins = useMemo(
    () => sortPins(filteredPins, sortMode),
    [filteredPins, sortMode]
  );

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const searchedPins = useMemo(() => {
    if (!normalizedQuery) return sortedPins;
    return sortedPins.filter((p) => {
      const haystack = [
        p.companyName,
        p.contactName,
        p.contactFullName,
        p.fullName,
        p.email,
        p.dealName,
        p.phone,
        p.website,
        p.domain,
        formatSyncTime(p.lastContactedAt),
        displayStageForPin(p),
        p.addressLine,
        p.address?.fullAddress,
        p.address?.street,
        p.address?.city,
        p.address?.postalCode,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [sortedPins, normalizedQuery]);

  const hqMatchesSearch = useMemo(() => {
    if (!normalizedQuery) return true;
    const haystack = [HQ.companyName, HQ.addressLine, "unser standort", "dr. hoffmann"]
      .join(" ")
      .toLowerCase();
    return haystack.includes(normalizedQuery);
  }, [normalizedQuery]);

  const mappablePins = useMemo(
    () => searchedPins.filter(hasMapCoords),
    [searchedPins]
  );

  const missingPins = useMemo(
    () => searchedPins.filter((p) => !hasMapCoords(p)),
    [searchedPins]
  );

  // Always mount the map (even while loading / with zero deal pins).
  useEffect(() => {
    if (!mapNodeRef.current || mapRef.current) return undefined;

    const map = L.map(mapNodeRef.current, {
      center: [HQ.lat, HQ.lng],
      zoom: 16,
      scrollWheelZoom: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    hqLayerRef.current = L.layerGroup().addTo(map);
    userLayerRef.current = L.layerGroup().addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    routeLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    const kick = () => invalidateMapIfReady(map);
    const t1 = setTimeout(kick, 50);
    const t2 = setTimeout(kick, 250);
    window.addEventListener("resize", kick);

    return () => {
      window.removeEventListener("resize", kick);
      clearTimeout(t1);
      clearTimeout(t2);
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
      hqLayerRef.current = null;
      userLayerRef.current = null;
      routeLayerRef.current = null;
      markersRef.current = new Map();
    };
  }, []);

  // Fixed HQ pin — always present, visually distinct from deal pins.
  useEffect(() => {
    const map = mapRef.current;
    const hqLayer = hqLayerRef.current;
    if (!map || !hqLayer) return;

    hqLayer.clearLayers();
    const marker = L.marker([HQ.lat, HQ.lng], {
      icon: makeHqIcon(),
      zIndexOffset: 1000,
    });
    marker.bindPopup(buildPopupHtml(HQ, t, true));
    marker.on("click", () => setSelectedId(HQ.id));
    marker.addTo(hqLayer);
    markersRef.current.set(HQ.id, marker);
  }, [t]);

  // User location pin
  useEffect(() => {
    const map = mapRef.current;
    const userLayer = userLayerRef.current;
    if (!map || !userLayer) return;

    userLayer.clearLayers();
    markersRef.current.delete("user-location");

    if (
      !userLocation ||
      !Number.isFinite(userLocation.lat) ||
      !Number.isFinite(userLocation.lng)
    ) {
      return;
    }

    const marker = L.marker([userLocation.lat, userLocation.lng], {
      icon: makeUserIcon(),
      zIndexOffset: 1200,
    });
    marker.bindPopup(`<strong>${escapeHtml(t("dealMap.userLocation"))}</strong>`);
    marker.on("click", () => setSelectedId("user-location"));
    marker.addTo(userLayer);
    markersRef.current.set("user-location", marker);
  }, [userLocation, t]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();
    for (const key of [...markersRef.current.keys()]) {
      if (key !== HQ.id && key !== "user-location") markersRef.current.delete(key);
    }

    mappablePins.forEach((pin) => {
      if (!hasMapCoords(pin)) return;
      const color = colorForPin(pin);
      const marker = L.marker([Number(pin.lat), Number(pin.lng)], {
        icon: makeStageIcon(color),
      });
      marker.bindPopup(buildPopupHtml(pin, t));
      marker.on("click", () => setSelectedId(pin.id));
      marker.addTo(layer);
      markersRef.current.set(pin.id, marker);
    });

    // Keep the default view on Cremon 11 — do not fitBounds to all pins.
    const timer = setTimeout(() => invalidateMapIfReady(map), 50);
    return () => clearTimeout(timer);
  }, [mappablePins, t]);

  // Draw route segments: dark blue → light blue across the loop
  useEffect(() => {
    const routeLayer = routeLayerRef.current;
    if (!routeLayer) return;

    routeLayer.clearLayers();
    if (!routePlan?.origin || !routePlan.stops?.length) return;

    const points = [
      { lat: routePlan.origin.lat, lng: routePlan.origin.lng },
      ...routePlan.stops.map((s) => ({ lat: s.lat, lng: s.lng })),
      { lat: routePlan.origin.lat, lng: routePlan.origin.lng },
    ];

    const totalSegments = points.length - 1;

    // Interpolate hex channel from blue (28,42,142) to red (220,38,38)
    const lerp = (a, b, t) => Math.round(a + (b - a) * t);
    const segmentColor = (i) => {
      const t = totalSegments <= 1 ? 0 : i / (totalSegments - 1);
      const r = lerp(28, 220, t);
      const g = lerp(42, 38, t);
      const b = lerp(142, 38, t);
      return `rgb(${r},${g},${b})`;
    };

    for (let i = 0; i < totalSegments; i += 1) {
      const a = points[i];
      const b = points[i + 1];
      L.polyline([[a.lat, a.lng], [b.lat, b.lng]], {
        color: segmentColor(i),
        weight: 4,
        opacity: 0.95,
        dashArray: "8 6",
        lineCap: "round",
      }).addTo(routeLayer);
    }
  }, [routePlan]);

  const focusPin = (pin) => {
    setSelectedId(pin.id);
    const map = mapRef.current;
    if (!map || !hasMapCoords(pin)) {
      return;
    }
    map.setView([pin.lat, pin.lng], pin.id === HQ.id ? 16 : 14);
    const marker = markersRef.current.get(pin.id);
    if (marker) marker.openPopup();
  };

  const generateRoute = (originMode = "user") => {
    setRouteMessage(null);
    setRoutePlan(null);

    const origin =
      originMode === "hq"
        ? {
            lat: HQ.lat,
            lng: HQ.lng,
            label: t("dealMap.hqName"),
            addressLine: HQ.addressLine,
          }
        : userLocation && Number.isFinite(userLocation.lat) && Number.isFinite(userLocation.lng)
          ? {
              lat: userLocation.lat,
              lng: userLocation.lng,
              label: t("dealMap.userLocation"),
              addressLine: "",
            }
          : null;

    if (!origin) {
      setRouteMessage(t("dealMap.routeNeedLocation"));
      requestUserLocation();
      return;
    }

    if (!mappablePins.length) {
      setRouteMessage(t("dealMap.routeNoPins"));
      return;
    }

    const uniqueLocations = groupPinsByLocation(mappablePins);
    const stops = buildOptimizedRoute(origin, uniqueLocations, MAX_ROUTE_STOPS);
    if (!stops.length) {
      setRouteMessage(t("dealMap.routeNoPins"));
      return;
    }

    const urls = buildRouteMapUrls(origin, stops);
    setRoutePlan({
      originMode,
      origin,
      stops,
      ...urls,
    });
    setRouteMessage(
      t(originMode === "hq" ? "dealMap.routeReadyFromHq" : "dealMap.routeReady", {
        count: stops.length,
      })
    );
    window.open(urls.googleMapsUrl, "_blank", "noopener,noreferrer");
  };

  const applyRouteStops = (origin, stops, originMode = routePlan?.originMode) => {
    if (!stops.length) {
      setRoutePlan(null);
      setRouteMessage(null);
      return;
    }
    const urls = buildRouteMapUrls(origin, stops);
    setRoutePlan({
      ...(routePlan || {}),
      originMode,
      origin,
      stops,
      ...urls,
    });
    setRouteMessage(
      t(originMode === "hq" ? "dealMap.routeReadyFromHq" : "dealMap.routeReady", {
        count: stops.length,
      })
    );
  };

  const removeRouteStop = (stopKey) => {
    if (!routePlan) return;
    applyRouteStops(
      routePlan.origin,
      routePlan.stops.filter((s) => s.key !== stopKey),
      routePlan.originMode
    );
  };

  const removeRouteOrigin = () => {
    if (!routePlan?.stops?.length) {
      setRoutePlan(null);
      setRouteMessage(null);
      return;
    }
    const [nextOrigin, ...stops] = routePlan.stops;
    applyRouteStops(originFromRouteStop(nextOrigin, t), stops, "stop");
  };

  const clearRoute = () => {
    setRoutePlan(null);
    setRouteMessage(null);
  };

  const zoomToUserLocation = () => {
    setRouteMessage(null);
    if (!userLocation || !Number.isFinite(userLocation.lat) || !Number.isFinite(userLocation.lng)) {
      setRouteMessage(t("dealMap.routeNeedLocation"));
      requestUserLocation();
      return;
    }
    const map = mapRef.current;
    if (!map) return;
    map.setView([userLocation.lat, userLocation.lng], 16);
    setSelectedId("user-location");
    const marker = markersRef.current.get("user-location");
    if (marker) marker.openPopup();
  };

  const toggleMapFullscreen = async () => {
    const shell = mapShellRef.current;
    if (!shell) return;

    if (isMapFullscreen) {
      if (getFullscreenElement()) {
        await exitFullscreen();
      }
      nativeFullscreenRef.current = false;
      setIsMapFullscreen(false);
      return;
    }

    applyMapFullscreenViewport();
    setIsMapFullscreen(true);
    try {
      await requestFullscreen(shell);
      nativeFullscreenRef.current = getFullscreenElement() === shell;
    } catch {
      nativeFullscreenRef.current = false;
    }
    setTimeout(() => invalidateMapIfReady(mapRef.current), 80);
  };

  return (
    <div className="team-deal-map">
      <div className="team-deal-map-toolbar">
        <div>
          <h2>{t("dealMap.title")}</h2>
          <p className="team-muted">{t("dealMap.lead")}</p>
          {lastSyncedLabel && (
            <p className="team-muted team-deal-map-sync">
              {t("dealMap.lastSynced", { time: lastSyncedLabel })}
            </p>
          )}
        </div>
        <div className="team-deal-map-actions">
          <label className="team-deal-map-filter">
            <span>{t("dealMap.stageFilter")}</span>
            <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
              <option value="all">{t("common.all")}</option>
              {legendStages.map((stage) => (
                <option key={stage} value={stage}>
                  {stage === UNKNOWN_STAGE_LABEL ? t("dealMap.unknownStage") : stage}
                </option>
              ))}
            </select>
          </label>
          <label className="team-deal-map-filter">
            <span>{t("dealMap.sortLabel")}</span>
            <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
              <option value="name-asc">{t("dealMap.sortNameAsc")}</option>
              <option value="last-contacted-desc">{t("dealMap.sortLastContactDesc")}</option>
              <option value="last-contacted-asc">{t("dealMap.sortLastContactAsc")}</option>
            </select>
          </label>
          <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
            {loading ? t("common.loading") : t("dealMap.refresh")}
          </button>
        </div>
      </div>

      <div className="team-deal-map-stats">
        <span>{t("dealMap.statTotal", { count: searchedPins.length })}</span>
        <span>{t("dealMap.statMapped", { count: mappablePins.length })}</span>
        <span>{t("dealMap.statMissing", { count: missingPins.length })}</span>
      </div>

      <div className="team-deal-map-rotate-hint" role="note">
        <span className="team-deal-map-rotate-phone" aria-hidden="true">
          <ScreenRotationIcon fontSize="small" />
        </span>
        <span>{t("dealMap.rotateHint")}</span>
      </div>

      {error && <div className="auth-alert is-error">{error}</div>}

      <div className="team-deal-map-layout">
        <div
          ref={mapShellRef}
          className={`team-deal-map-canvas-wrap${isMapImmersive ? " is-fullscreen" : ""}`}
        >
          <div className="team-deal-map-overlay-controls">
            <button
              type="button"
              className="team-deal-map-location-btn"
              onClick={zoomToUserLocation}
            >
              <MyLocationIcon fontSize="small" />
              <span>{t("dealMap.zoomToMe")}</span>
            </button>
            <button
              type="button"
              className="team-deal-map-fullscreen-btn"
              onClick={toggleMapFullscreen}
              aria-label={
                isMapFullscreen
                  ? t("dealMap.fullscreenExit")
                  : t("dealMap.fullscreenEnter")
              }
              title={
                isMapFullscreen
                  ? t("dealMap.fullscreenExit")
                  : t("dealMap.fullscreenEnter")
              }
            >
              {isMapFullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </button>
          </div>
          <div className="team-deal-map-rotate-hint team-deal-map-rotate-hint--map" role="note">
            <span className="team-deal-map-rotate-phone" aria-hidden="true">
              <ScreenRotationIcon fontSize="small" />
            </span>
            <span>{t("dealMap.rotateHint")}</span>
          </div>
          <div ref={mapNodeRef} className="team-deal-map-canvas" role="presentation" />
        </div>

        <div className="team-deal-map-route-bar">
          <button
            type="button"
            className="btn-primary team-deal-map-route-btn"
            onClick={() => generateRoute("user")}
          >
            {t("dealMap.routeGenerate")}
          </button>
          <button
            type="button"
            className="btn-ghost team-deal-map-route-btn"
            onClick={() => generateRoute("hq")}
          >
            {t("dealMap.routeGenerateFromHq")}
          </button>
          <button type="button" className="btn-ghost" onClick={zoomToUserLocation}>
            {t("dealMap.zoomToMe")}
          </button>
          {geoStatus === "denied" && (
            <button type="button" className="btn-ghost" onClick={requestUserLocation}>
              {t("dealMap.locationRetry")}
            </button>
          )}
          {geoStatus === "pending" && !userLocation && (
            <span className="team-muted">{t("dealMap.locationPending")}</span>
          )}
          {geoStatus === "granted" && userLocation && (
            <span className="team-muted">{t("dealMap.locationReady")}</span>
          )}
          {(geoStatus === "denied" || geoStatus === "unavailable") && (
            <span className="team-deal-map-warn">{t("dealMap.locationDenied")}</span>
          )}
        </div>
        {routeMessage && <p className="team-muted team-deal-map-route-msg">{routeMessage}</p>}

        {routePlan && (
          <section className="team-deal-map-route-list" aria-label={t("dealMap.routeListAria")}>
            <div className="team-deal-map-route-list-header">
              <h3>{t("dealMap.routeListTitle")}</h3>
              <div className="team-deal-map-route-list-actions">
                <a
                  className="btn-ghost"
                  href={routePlan.appleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("dealMap.routeOpenApple")}
                </a>
                <a
                  className="btn-primary"
                  href={routePlan.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("dealMap.routeOpenGoogle")}
                </a>
                <button type="button" className="btn-ghost" onClick={clearRoute}>
                  {t("dealMap.routeClear")}
                </button>
              </div>
            </div>
            <ol>
              <li
                className="team-deal-map-route-stop is-start"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  if (e.target.closest(".team-deal-map-route-remove")) return;
                  const map = mapRef.current;
                  if (map) map.setView([routePlan.origin.lat, routePlan.origin.lng], 16);
                }}
              >
                <span className="team-deal-map-route-stop-num">1</span>
                <span className="team-deal-map-route-stop-copy">
                  <strong>{routePlan.origin.label}</strong>
                  {routePlan.origin.addressLine ? (
                    <span>{routePlan.origin.addressLine}</span>
                  ) : (
                    <span>{t("dealMap.routeStart")}</span>
                  )}
                </span>
                <button
                  type="button"
                  className="team-deal-map-route-remove"
                  onClick={removeRouteOrigin}
                  aria-label={t("dealMap.routeRemoveStop")}
                  title={t("dealMap.routeRemoveStop")}
                >
                  ×
                </button>
              </li>
              {routePlan.stops.map((stop, index) => (
                <li
                  key={stop.key || `${stop.lat},${stop.lng},${index}`}
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    if (e.target.closest(".team-deal-map-route-remove")) return;
                    const map = mapRef.current;
                    if (map && Number.isFinite(stop.lat) && Number.isFinite(stop.lng)) {
                      map.setView([stop.lat, stop.lng], 16);
                    }
                  }}
                >
                  <span className="team-deal-map-route-stop-num">{index + 2}</span>
                  <span className="team-deal-map-route-stop-copy">
                    <strong className="team-deal-map-route-companies">
                      {stop.companies.map((c, companyIdx) => (
                        <span key={c.id || `${stop.key}-${companyIdx}`}>
                          {c.companyName || t("dealMap.unnamed")}
                        </span>
                      ))}
                    </strong>
                    <span>{stop.addressLine || t("dealMap.noAddress")}</span>
                    {stop.companies.length > 1 && (
                      <em>
                        {t("dealMap.routeCompaniesAtStop", {
                          count: stop.companies.length,
                        })}
                      </em>
                    )}
                  </span>
                  <button
                    type="button"
                    className="team-deal-map-route-remove"
                    onClick={() => removeRouteStop(stop.key)}
                    aria-label={t("dealMap.routeRemoveStop")}
                    title={t("dealMap.routeRemoveStop")}
                  >
                    ×
                  </button>
                </li>
              ))}
              <li
                className="team-deal-map-route-stop is-end"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  const map = mapRef.current;
                  if (map) map.setView([routePlan.origin.lat, routePlan.origin.lng], 16);
                }}
              >
                <span className="team-deal-map-route-stop-num">
                  {routePlan.stops.length + 2}
                </span>
                <span className="team-deal-map-route-stop-copy">
                  <strong>{routePlan.origin.label}</strong>
                  <span>{t("dealMap.routeReturn")}</span>
                </span>
              </li>
            </ol>
          </section>
        )}

        <div className="team-deal-map-legend" aria-label={t("dealMap.legendAria")}>
          <span>
            <i className="team-deal-map-legend-user" aria-hidden="true" />
            {t("dealMap.userLocation")}
          </span>
          <span>
            <i className="team-deal-map-legend-hq" aria-hidden="true" />
            {t("dealMap.hqLabel")}
          </span>
          {legendStages.map((stage) => (
            <span key={stage}>
              <i style={{ background: colorForStage(stage) }} aria-hidden="true" />
              {stage === LOST_STAGE_LABEL
                ? t("dealMap.lostStageLegend")
                : stage === UNKNOWN_STAGE_LABEL
                  ? t("dealMap.unknownStage")
                  : stage}
            </span>
          ))}
        </div>

        <aside className="team-deal-map-list">
          <div className="team-deal-map-list-header">
            <h3>{t("dealMap.listTitle")}</h3>
            <label className="team-deal-map-search">
              <span className="sr-only">{t("dealMap.searchLabel")}</span>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("dealMap.searchPlaceholder")}
                autoComplete="off"
              />
            </label>
          </div>
          <ul>
            {hqMatchesSearch && (
              <li>
                <button
                  type="button"
                  className={selectedId === HQ.id ? "is-active is-hq" : "is-hq"}
                  onClick={() => focusPin(HQ)}
                >
                  <span className="team-deal-map-dot team-deal-map-dot--hq" aria-hidden="true" />
                  <span className="team-deal-map-list-copy">
                    <strong>{t("dealMap.hqName")}</strong>
                    <span>{HQ.addressLine}</span>
                    <em>{t("dealMap.hqLabel")}</em>
                  </span>
                </button>
              </li>
            )}
            {searchedPins.map((pin) => {
              const hasCoords = hasMapCoords(pin);
              return (
                <li key={pin.id}>
                  <div className={`team-deal-map-list-card ${selectedId === pin.id ? "is-active" : ""}`}>
                    <button
                      type="button"
                      className="team-deal-map-list-main"
                      onClick={() => focusPin(pin)}
                    >
                      <span
                        className="team-deal-map-dot"
                        style={{
                          background: colorForPin(pin),
                        }}
                        aria-hidden="true"
                      />
                      <span className="team-deal-map-list-copy">
                        <strong>{pinDisplayName(pin) || t("dealMap.unnamed")}</strong>
                        <span>
                          {pin.addressLine || pin.address?.fullAddress || t("dealMap.noAddress")}
                        </span>
                        <span>
                          {pin.phone
                            ? `${t("dealMap.phone")}: ${pin.phone}`
                            : t("dealMap.noPhone")}
                        </span>
                        <span>
                          {`${t("dealMap.lastContactedAt")}: ${
                            formatSyncTime(pin.lastContactedAt) || t("dealMap.noLastContact")
                          }`}
                        </span>
                        <span>
                          {pinWebsiteValue(pin)
                            ? `${t("dealMap.website")}: ${websiteLabel(pinWebsiteValue(pin))}`
                            : `${t("dealMap.website")}: ${t("dealMap.noWebsite")}`}
                        </span>
                        <em>{displayStageForPin(pin) || t("dealMap.unknownStage")}</em>
                        {!hasCoords && (
                          <span className="team-deal-map-warn">{t("dealMap.notGeocoded")}</span>
                        )}
                      </span>
                    </button>
                    <label className="team-deal-map-stage-editor">
                      <span>{t("dealMap.stage")}</span>
                      <select
                        className="team-status-select"
                        value={stageValueForPin(pin)}
                        disabled={stageSavingId === pin.id}
                        onChange={(event) => updatePinStage(pin.id, event.target.value)}
                      >
                        {CRM_STAGE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {t(`customerStatus.${option}`)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </li>
              );
            })}
            {!searchedPins.length && !hqMatchesSearch && (
              <li>
                <p className="team-muted team-deal-map-list-empty">
                  {normalizedQuery ? t("dealMap.searchEmpty") : t("dealMap.listEmpty")}
                </p>
              </li>
            )}
            {!searchedPins.length && !normalizedQuery && hqMatchesSearch && (
              <li>
                <p className="team-muted team-deal-map-list-empty">{t("dealMap.listEmpty")}</p>
              </li>
            )}
          </ul>
        </aside>
      </div>
    </div>
  );
};

export default DealMapTab;
